/**
 * Results Table - Test sonuçlarını tablo formatında gösteren bileşen
 * 
 * Features:
 * - Multi-variant results display
 * - Statistical significance indicators
 * - Winner probability display
 * - Additional metrics (extra transactions, revenue)
 * - Interactive actions (chart viewing)
 */

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import {
  formatNumber,
  formatPercentage,
  formatPValue,
  formatCurrency
} from '../../utils/statisticsUtils';

const ResultsTable = ({
  postTestResults,
  postTestSettings,
  onShowChart,
  resultsTableRef
}) => {
  if (!postTestResults) return null;

  return (
    <Card ref={resultsTableRef}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
          Detaylı Sonuç Tablosu
        </Typography>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Varyant</TableCell>
                <TableCell align="right">Sessions</TableCell>
                <TableCell align="right">Conversions</TableCell>
                <TableCell align="right">Conversion Rate</TableCell>
                <TableCell align="right">Lift</TableCell>
                <TableCell align="right">P-Value</TableCell>
                <TableCell align="center">Significance</TableCell>
                <TableCell align="center">Kazanma Olasılığı</TableCell>
                <TableCell align="center">Additional Days</TableCell>
                <TableCell align="right">Monthly Extra Transactions</TableCell>
                <TableCell align="right">Yearly Extra Transactions</TableCell>
                {postTestSettings.averageOrderValue > 0 && (
                  <>
                    <TableCell align="right">Monthly Extra Revenue</TableCell>
                    <TableCell align="right">Yearly Extra Revenue</TableCell>
                  </>
                )}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Control Row */}
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {postTestResults.control.name}
                    <Chip label="Control" size="small" color="primary" />
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {formatNumber(postTestResults.control.sessions)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(postTestResults.control.conversions)}
                </TableCell>
                <TableCell align="right">
                  {formatPercentage(postTestResults.control.conversionRate)}
                </TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="center">—</TableCell>
                <TableCell align="center">
                  {postTestSettings.analysisMethod === 'bayesian' && postTestResults.variants.length > 0 ? (
                    <Typography 
                      variant="body2" 
                      color="primary.main"
                      fontWeight="bold"
                    >
                      {(() => {
                        // Get control's win probability from first variant's pairwise comparison
                        const firstVariant = postTestResults.variants[0];
                        if (firstVariant?.enhancedBayesian?.winnerProbabilities) {
                          const controlProb = firstVariant.enhancedBayesian.winnerProbabilities.find(p => p.isControl);
                          return formatPercentage(controlProb?.winProbability || 0);
                        }
                        return '—';
                      })()}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="center">—</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
                {postTestSettings.averageOrderValue > 0 && (
                  <>
                    <TableCell align="right">—</TableCell>
                    <TableCell align="right">—</TableCell>
                  </>
                )}
                <TableCell align="center">—</TableCell>
              </TableRow>

              {/* Variant Rows */}
              {postTestResults.variants.map((variant, index) => (
                <TableRow key={index}>
                  <TableCell>{variant.name}</TableCell>
                  <TableCell align="right">
                    {formatNumber(variant.sessions)}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(variant.conversions)}
                  </TableCell>
                  <TableCell align="right">
                    {formatPercentage(variant.conversionRate)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      color={variant.liftPercentage > 0 ? 'success.main' : 'error.main'}
                      fontWeight="bold"
                    >
                      {variant.liftPercentage > 0 ? '+' : ''}{variant.liftPercentage.toFixed(2)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {variant.statisticalTest && 
                      formatPValue(variant.statisticalTest.pValue || variant.statisticalTest.probabilityVariantWins)
                    }
                  </TableCell>
                  <TableCell align="center">
                    {variant.statisticalTest && (
                      <Chip
                        label={variant.statisticalTest.isSignificant ? 'Significant' : 'Not Significant'}
                        color={(() => {
                          if (!variant.statisticalTest.isSignificant) return 'default';
                          
                          // Check win probability for significant results
                          if (variant.enhancedBayesian?.winnerProbabilities) {
                            const variantProb = variant.enhancedBayesian.winnerProbabilities.find(p => !p.isControl);
                            const winProbability = variantProb?.winProbability || 0;
                            // If win probability is below 5%, show as negative significant (red)
                            return winProbability < 0.05 ? 'error' : 'success';
                          }
                          
                          return 'success';
                        })()}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {postTestSettings.analysisMethod === 'bayesian' && variant.enhancedBayesian?.winnerProbabilities ? (
                      <Typography 
                        variant="body2" 
                        color="secondary.main"
                        fontWeight="bold"
                      >
                        {(() => {
                          // Get variant's win probability from its own pairwise comparison with control
                          const variantProb = variant.enhancedBayesian.winnerProbabilities.find(p => !p.isControl);
                          return formatPercentage(variantProb?.winProbability || 0);
                        })()}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {variant.additionalDays && (
                      <Typography 
                        variant="body2" 
                        color={variant.additionalDays.additionalDays === 0 ? 'success.main' : 'warning.main'}
                        fontWeight="bold"
                      >
                        {variant.additionalDays.additionalDays === Infinity ? '∞' : variant.additionalDays.additionalDays}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {variant.extraTransactions && (
                      <Typography variant="body2" color="success.main" fontWeight="bold">
                        {formatNumber(variant.extraTransactions.monthlyExtraTransactions, 0)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {variant.extraTransactions && (
                      <Typography variant="body2" color="success.main" fontWeight="bold">
                        {formatNumber(variant.extraTransactions.yearlyExtraTransactions, 0)}
                      </Typography>
                    )}
                  </TableCell>
                  {postTestSettings.averageOrderValue > 0 && (
                    <>
                      <TableCell align="right">
                        {variant.extraTransactions && (
                          <Typography variant="body2" color="success.main" fontWeight="bold">
                            {formatCurrency(variant.extraTransactions.monthlyExtraRevenue, 'TRY')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {variant.extraTransactions && (
                          <Typography variant="body2" color="success.main" fontWeight="bold">
                            {formatCurrency(variant.extraTransactions.yearlyExtraRevenue, 'TRY')}
                          </Typography>
                        )}
                      </TableCell>
                    </>
                  )}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* Show Probability Density Chart */}
                      {variant.enhancedBayesian?.densityData && (
                        <Tooltip title="Show Probability Density Chart">
                          <IconButton 
                            size="small" 
                            color="secondary"
                            onClick={() => onShowChart(variant)}
                          >
                            <AssessmentIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 3 }}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Analiz Yöntemi:</strong> {postTestSettings.analysisMethod.toUpperCase()}<br/>
              <strong>Güven Seviyesi:</strong> {formatPercentage(postTestSettings.confidenceLevel)}<br/>
              <strong>Test Süresi:</strong> {postTestSettings.testDuration} gün<br/>
              <strong>Günlük Trafik:</strong> {formatNumber(postTestSettings.dailyTraffic)}<br/>
              <strong>Trafik Dağılımı:</strong> {postTestSettings.trafficSplit}%
              {postTestSettings.averageOrderValue > 0 && (
                <>
                  <br/><strong>Ortalama Sipariş Değeri:</strong> {formatCurrency(postTestSettings.averageOrderValue, 'TRY')}
                </>
              )}
            </Typography>
          </Alert>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ResultsTable; 